import React, { Fragment, useState , useEffect, useMemo} from "react";
import { Col, Card, Form, Spinner} from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import Snackbars from "../../../utils/Snackbars";
import useRequests from "../../../utils/useRequests";
import DataTable from "react-data-table-component";


const Analytics = () => {
    const location = useLocation();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const { getRequest } = useRequests();
    const [searchQuery, setSearchQuery] = useState('');
    const [dateRange, setDateRange] = useState('all');
    // Store purchase tickets and compute total ticket sales filtered by dateRange
    const [purchaseTickets, setPurchaseTickets] = useState([]);

    function Widget({ number, title }) {
      return (
        <div className="media-body">
          <h2 className="fs-38 text-black font-w600">{number}</h2>
          <span className="fs-18">{title}</span>
        </div>
      );
    }

    const formatTwo = (v) => {
      const n = Number(v);
      if (!isFinite(n)) return '0.00';
      return n.toFixed(2);
    };


    const [AlertData, setAlertData] = React.useState({
            variant: '',
            message: '',
          });
        
          const resetAlertData = () => {
            setTimeout(() => {
              setAlertData((AlertData) => ({
                ...AlertData,
                variant: '',
                message: '',
              }));
            }, process.env.REACT_APP_ALERT_AUTO_CLOSE);
          };

          const fetchEvents = async () => {
                              const response = await getRequest("event-list"); // Replace "users" with the appropriate API endpoint
                              if (response && response.code === 200) {
                                setEvents(response.data.data);
                              } else {
                                console.error("Failed to fetch events:", response.message);
                                setAlertData((AlertData) => ({
                                  ...AlertData,
                                  variant: 'error',
                                  message: 'Failed to fetch events',
                                }));
                              }
                              resetAlertData();
                  };
                
    // Fetch all dashboard data and events
    useEffect(() => {
      // Keep only purchase-tickets to compute total ticket sales
      const fetchResaleTickets = async () => {
        try {
          const r = await getRequest(`purchase-tickets`);
          if (r && r.code === 200 && Array.isArray(r.data)) {
            setPurchaseTickets(r.data);
          }
        } catch (err) {
          console.error('purchase-tickets error', err);
        }
      };

      const fetchAllStats = async () => {
        setLoading(true);
        try {
          await Promise.all([fetchEvents(), fetchResaleTickets()]);
        } finally {
          setLoading(false);
        }
      };

      fetchAllStats();

      if (location.state && typeof location.state.message !== 'undefined') {
        setAlertData((AlertData) => ({
          ...AlertData,
          variant: location.state.status,
          message: location.state.message,
        }));
        resetAlertData();
      }
    }, []);


    // Helpers for date filtering (manual parser for 'YYYY-MM-DD HH:mm:ss' and tolerant of ISO variants)
    const parseDateTime = (s) => {
      if (!s) return null;
      const str = String(s).trim();

      // If it looks like an ISO string (contains 'T' or timezone 'Z' or +HH:MM/-HH:MM), let Date parse it
      if (/T|Z|[+-]\d{2}:?\d{2}$/.test(str) || str.indexOf('T') >= 0) {
        const iso = str.indexOf(' ') >= 0 ? str.replace(' ', 'T') : str;
        const d = new Date(iso);
        if (!isNaN(d.getTime())) return d;
      }

      // Accept 'YYYY-MM-DD' or 'YYYY-MM-DD HH:mm:ss' with optional fractional seconds
      const m = str.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?)?$/);
      if (!m) return null;
      const year = parseInt(m[1], 10);
      const month = parseInt(m[2], 10) - 1;
      const day = parseInt(m[3], 10);
      const hour = m[4] ? parseInt(m[4], 10) : 0;
      const minute = m[5] ? parseInt(m[5], 10) : 0;
      const second = m[6] ? parseInt(m[6], 10) : 0;
      const ms = m[7] ? Math.floor(Number('0.' + m[7]) * 1000) : 0;
      const d = new Date(year, month, day, hour, minute, second, ms);
      return isNaN(d.getTime()) ? null : d;
    };

    const eventOverlapsRange = (event, rangeStart, rangeEnd) => {
      const s = parseDateTime(event.start_datetime || event.start_date || event.start);
      const e = parseDateTime(event.end_datetime || event.end_date || event.end) || s;
      if (!s) return false;
      return s <= rangeEnd && e >= rangeStart;
    };

    // Map a purchase to its event by common id fields
    const findEventForPurchase = (p) => {
      if (!p || !Array.isArray(events)) return null;
      const keys = [p.event_id, p.e_id, p.event_eid, p.eid, p.event];
      for (const key of keys) {
        if (key === undefined || key === null) continue;
        const sk = String(key);
        const ev = events.find(ev => [ev.e_id, ev.id, ev.event_id, ev.eid].some(k => k !== undefined && String(k) === sk));
        if (ev) return ev;
      }
      return null;
    };

    const now = new Date();
    let rangeStart = null;
    let rangeEnd = null;
    if (dateRange === 'today') {
      rangeStart = new Date(now);
      rangeStart.setHours(0,0,0,0);
      rangeEnd = new Date(now);
      rangeEnd.setHours(23,59,59,999);
    } else if (dateRange === '7') {
      rangeEnd = new Date(now);
      rangeStart = new Date(rangeEnd.getTime() - 7 * 24 * 60 * 60 * 1000);
    } else if (dateRange === '30') {
      rangeEnd = new Date(now);
      rangeStart = new Date(rangeEnd.getTime() - 30 * 24 * 60 * 60 * 1000);
    }

    // Count of events based only on the selected date range (used by widget)
    const eventsCount = Array.isArray(events)
      ? (dateRange === 'all'
          ? events.length
          : (rangeStart && rangeEnd ? events.filter(ev => eventOverlapsRange(ev, rangeStart, rangeEnd)).length : events.length))
      : 0;

    // Compute total ticket sales based on selected date range using `event_ticket_date` from purchase data
    const totalTicketsPrice = useMemo(() => {
      if (!Array.isArray(purchaseTickets) || purchaseTickets.length === 0) return 0;
      return purchaseTickets.reduce((acc, p) => {
        const dateStr = p.event_ticket_date;
        console.log(dateStr)
        const dt = parseDateTime(dateStr);
        const price = Number(p.purchase_price || 0);

        if (dt) {
          if (dateRange === 'all') return acc + price;
          if (rangeStart && rangeEnd && dt >= rangeStart && dt <= rangeEnd) return acc + price;
          return acc;
        }

        // No explicit ticket date — first check purchase's own start/end, then fall back to event's start/end
        if (dateRange === 'all') return acc + price;
        const pStart = parseDateTime(p.start_datetime || p.start_date || p.start);
        const pEnd = parseDateTime(p.end_datetime || p.end_date || p.end);
        if (pStart && pEnd && rangeStart && rangeEnd) {
          if (pStart <= rangeEnd && pEnd >= rangeStart) return acc + price;
        }
        const ev = findEventForPurchase(p);
        if (ev && rangeStart && rangeEnd && eventOverlapsRange(ev, rangeStart, rangeEnd)) {
          return acc + price;
        }
        return acc;
      }, 0);
    }, [purchaseTickets, dateRange, rangeStart, rangeEnd, events]);

    // Compute total tickets sold (quantity) based on selected date range
    const totalTicketsSold = useMemo(() => {
      if (!Array.isArray(purchaseTickets) || purchaseTickets.length === 0) return 0;
      return purchaseTickets.reduce((acc, p) => {
        const dateStr = p.event_ticket_date;
        const dt = parseDateTime(dateStr);
        const qty = Number(p.quantity || p.qty || p.ticket_quantity || 0);

        if (dt) {
          if (dateRange === 'all') return acc + qty;
          if (rangeStart && rangeEnd && dt >= rangeStart && dt <= rangeEnd) return acc + qty;
          return acc;
        }

        if (dateRange === 'all') return acc + qty;
        const pStartQ = parseDateTime(p.start_datetime || p.start_date || p.start);
        const pEndQ = parseDateTime(p.end_datetime || p.end_date || p.end);
        if (pStartQ && pEndQ && rangeStart && rangeEnd) {
          if (pStartQ <= rangeEnd && pEndQ >= rangeStart) return acc + qty;
        }
        const evQ = findEventForPurchase(p);
        if (evQ && rangeStart && rangeEnd && eventOverlapsRange(evQ, rangeStart, rangeEnd)) {
          return acc + qty;
        }
        return acc;
      }, 0);
    }, [purchaseTickets, dateRange, rangeStart, rangeEnd, events]);

    // Filter events based on search query and selected date range
    const filteredEvent = events.filter((event) => {
      const city = `${event.city || ''}`.toLowerCase();
      const name = `${event.name || ''}`.toLowerCase();
      const status = `${event.status || ''}`.toLowerCase();
      const searchLower = searchQuery.toLowerCase();

      const matchesSearch = city.includes(searchLower) || status.includes(searchLower) || name.includes(searchLower);
      const matchesDate = dateRange === 'all' ? true : (rangeStart && rangeEnd ? eventOverlapsRange(event, rangeStart, rangeEnd) : true);

      return matchesSearch && matchesDate;
    });





      const columns = [
              {
                name: "Event",
                selector: (row) => row.name,
                sortable: true,
                width:'500px'
                // whiteSpace:'normal !important'
              },
              {
                name: "City",
                selector: (row) => row.city,
                sortable: true,
                width:"400px"
              },
              
              {
                name: "Status",
                cell: (row) => (
                  <button
                    className={`btn ${
                      row.status === "Enable" ? "btn-success" : "btn-danger"
                    } btn-xxs`}
                  >
                    {row.status === "Enable" ? "Enable" : "Disable"}
                  </button>
                ),
                center: true,
                width: "400px",
              },
              {
                name: "Analytics",
                cell: (row) => (
                  <div className="text-center">
                    <Link to={`/event-analytics/${row.e_id}`} className="btn btn-primary btn-xxs me-2" title="Event Analytics"> 
                        <i className="fa-regular fa-eye"></i> 
                    </Link>
                  </div>
                ),
                center: true,
                width: "200px",
              },
            ];
          
    
    const customStyles = {
    header: {
        style: {
          whiteSpace:"normal",
        // minHeight: "56px",
        // flexBasis:'auto !important',
        },
    },
    headCells: {
        style: {
        // flexBasis:'auto !important',
        color: "#341439",
        fontSize: "17px",
        fontWeight: "600",
        textTransform: "capitalize",
        paddingLeft: "8px", // Adjust padding
        paddingRight: "8px",
        },
    },
    cells: {
        style: {
        // flexBasis:'auto !important',
        fontSize: "16px",
        padding: "12px 9px",
        color: "#7E7E7E",
        
        },
    },
    pagination: {
        style: {
        fontSize: "16px",
        },
    },
    "& .hmVlQg div:first-child": {
    whiteSpace: "normal !important",
},
    };
    

    return (
        
      <Fragment>
        <div className='d-sm-flex d-block justify-content-between align-items-center mb-4'>
          <h2 className='text-black font-w600 mb-0'>Analytics</h2>
          {AlertData.message !== "" ? (
            <Snackbars
              variant={AlertData.variant}
              message={AlertData.message}
            />
          ) : (
            ""
          )}
        </div>
        {/* Search input and date-range filter */}
        <div className='mb-4 d-sm-flex d-block justify-content-end'>
          <div style={{display: 'flex', gap: '0.75rem', alignItems: 'center'}}>
                        <div style={{minWidth: '18rem'}}>
              <Form.Select
                aria-label='Filter by date range'
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                style={{width: '100%', height: '3.2rem', fontSize: '16px', border: '1px solid #ced4da', borderRadius: '0.625rem', padding: '0 .75rem'}}
              >
                <option value='all'>All Events</option>
                <option value='today'>Today</option>
                <option value='7'>Last 7 Days</option>
                <option value='30'>Last 30 Days</option>
              </Form.Select>
            </div>
            <div style={{position: "relative", width: "18rem"}}>
              <Form.Control
                type='text'
                placeholder='Search...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: "100%",
                  fontSize: "18px",
                  fontWeight: 500,
                  paddingRight: "2rem",
                  borderRadius: "0.625rem",
                  height: "3.2rem",
                }}
              />
              <i
                className='fa fa-search'
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#6c757d",
                  pointerEvents: "none",
                }}
              ></i>
            </div>


          </div>
        </div>
        {/* Dashboard widgets (same as Home) */}
        {!loading && (
          <div className="row mb-4">
            <div className="col-xl-4 col-sm-6">
              <div className="card">
                <div className="card-body">
                  <div className="media align-items-center">
                    <Widget number={`${eventsCount}`} title="Total Events" />
                    <span className="p-3 border ms-3 rounded-circle">
                      <i className="far fa-calendar-alt"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-4 col-sm-6">
              <div className="card">
                <div className="card-body">
                  <div className="media align-items-center">
                    <Widget number={totalTicketsSold} title="Total Tickets Sold" />
                    <span className="p-3 border ms-3 rounded-circle">
                      <i className="fas fa-ticket-alt"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-4 col-sm-6">
              <div className="card">
                <div className="card-body">
                  <div className="media align-items-center">
                    <Widget number={`Rs ${formatTwo(totalTicketsPrice)}`} title="Total Ticket Sales" />
                    <span className="p-3 border ms-3 rounded-circle">
                      <i className="far fa-money-bill-alt"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}


        <Col lg={12}> 
        <Card>
          <Card.Body>
            {loading ? (
              <div className='text-center'>
                <Spinner animation='border'variant="primary"  />
                <p>Loading Analytics...</p>
              </div>
            ) : (
              <DataTable
                columns={columns}
                data={filteredEvent} // Use filtered data
                customStyles={customStyles}
                pagination
                highlightOnHover
                responsive
              />
            )}
          </Card.Body>
        </Card>
        </Col>
      </Fragment>
    );
};

export default Analytics;
